import { useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router";
import { noteRedacted, visibleBody } from "../api/redaction";
import type { Moment, Performance, Post, SavedInput } from "../api/types";
import { useCommands, useGate, useMe, useServices } from "../app/services";
import { useAction } from "../app/useAction";
import { usePlaybackActions } from "../app/usePlaybackActions";
import { UNAVAILABLE_TEXT } from "../api/errors";
import { kindLabel, performerNames } from "../lib/labels";
import { formatAgo, formatDate, formatMs, segmentLength, sourcePosition } from "../lib/time";
import { watchUrl } from "../lib/youtube";
import { isInFlight } from "../state/commands";
import type { SaveTarget } from "../state/saved";
import { useStore } from "../state/store";
import { Icon } from "./Icon";
import { SharePost } from "./SharePost";
import { Dialog, Field, Pill, Thumb } from "./ui";

/* ---------------------------------------------------------------- お気に入り・保存（サーバーの応答が返ってから押した状態にする） */

export function SaveButton({
  target,
  label,
  compact = false,
  performanceId,
  disabledReason,
}: {
  target: SavedInput;
  /** 何を保存するか（読み上げ用） */ label: string;
  compact?: boolean;
  /** Mix の既登録曲を、通常の保存一覧と照合する。 */
  performanceId?: string | null;
  disabledReason?: string;
}) {
  const { api, saved: index } = useServices();
  const state = useStore(index.store);
  const mix = target.mix_target;
  const key = mix ? JSON.stringify([mix.session_id, mix.generation, mix.mix_id, mix.plan_id, mix.occurrence_id]) : "";
  const [receipt, setReceipt] = useState<{ key: string; performanceId: string } | null>(null);
  const resolvedId = performanceId ?? (receipt?.key === key ? receipt.performanceId : null);
  const indexedTarget: SaveTarget | null = mix ? (resolvedId ? { performance_id: resolvedId } : null) : target as SaveTarget;
  const savedId = indexedTarget ? index.idFor(indexedTarget, state) : null;
  const saved = savedId !== null;
  const save = useAction("library.save", (fixedTarget: SavedInput) => api.save(fixedTarget));
  const unsave = useAction("library.unsave", (id: string) => api.unsave(id));
  const running = save.running || unsave.running;
  const connected = saved ? unsave.connected : save.connected;
  const checking = state.status === "loading" || state.status === "idle";
  const noun = target.post_id ? "投稿を保存" : "お気に入り";
  const unavailable = saved ? undefined : disabledReason ?? (mix && api.mode === "mock" ? "Mix中の保存は、この表示サンプルでは試せません。" : undefined);
  const onClick = async () => {
    if (unavailable) return;
    if (saved && indexedTarget) {
      const res = await unsave.run(savedId);
      if (res.ok) index.set(indexedTarget, null);
    } else {
      // The request owns this exact target, even if a new song arrives while awaiting HTTP.
      const fixedTarget = structuredClone(target);
      const res = await save.run(fixedTarget);
      if (res.ok) {
        index.set(fixedTarget.post_id ? { post_id: fixedTarget.post_id } : { performance_id: res.value.performance.id }, res.value.id);
        if (fixedTarget.mix_target) setReceipt({ key, performanceId: res.value.performance.id });
      }
    }
  };
  const error = save.error ?? unsave.error;
  return (
    <span className="save-btn-wrap">
      <button
        type="button"
        className={`btn btn-secondary btn-small save-btn${saved ? " save-btn--on" : ""}${compact ? " btn-compact" : ""}`}
        aria-pressed={saved}
        aria-busy={running || undefined}
        disabled={running || !connected || checking || state.status === "unavailable" || !!unavailable}
        onClick={onClick}
        aria-label={`${label}を${noun}${saved ? "（保存済み）" : ""}`}
        title={unavailable ?? (!connected ? "まだAPIに接続されていません" : state.status === "unavailable" ? "保存の状態を確認できません" : undefined)}
      >
        <Icon name={target.post_id ? "bookmark" : "heart"} className="btn-icon" />
        {compact ? null : saved ? "保存済み" : noun === "お気に入り" ? "お気に入り" : "保存"}
      </button>
      {error && (
        <span className="save-btn__error" role="alert">
          {error}
        </span>
      )}
    </span>
  );
}

/* ---------------------------------------------------------------- キューに追加（受付と結果は CommandTracker が出す） */

export function EnqueueButton({ performance, post, compact = false }: { performance: Performance; post?: Post | null; compact?: boolean }) {
  const gate = useGate("enqueue");
  const actions = usePlaybackActions();
  const cmds = useCommands();
  const describe = useId();
  const busy = cmds.some((c) => c.kind === "enqueue" && isInFlight(c) && c.label.includes(performance.work.title));
  const unavailable = !performance.available;
  const disabled = !gate.ok || busy || unavailable;
  const why = unavailable ? "元動画を利用できないため追加できません。" : gate.text;
  return (
    <>
      <button
        type="button"
        className={`btn btn-secondary btn-small${compact ? " btn-compact" : ""}`}
        onClick={() => actions.enqueue(performance, post)}
        disabled={disabled}
        aria-busy={busy || undefined}
        aria-describedby={disabled && why ? describe : undefined}
        aria-label={compact ? `「${performance.work.title}」をキューに追加` : undefined}
      >
        <Icon name="plus" className="btn-icon" />
        {compact ? null : busy ? "追加を受付中…" : "キューに追加"}
      </button>
      {disabled && why && (
        <span id={describe} className="visually-hidden">
          {why}
        </span>
      )}
    </>
  );
}

/* ---------------------------------------------------------------- 歌唱のカード（参照元の stream-card の並び：左に16:9の絵、右に文字） */

export function PerformanceCard({ performance: p, note }: { performance: Performance; note?: string }) {
  const length = segmentLength(p.segment);
  return (
    <li className={`perf-card${p.available ? "" : " perf-card--unavailable"}`}>
      <Link to={`/performances/${p.id}`} className="perf-card__thumb" tabIndex={-1} aria-hidden="true">
        <Thumb videoId={p.segment.video_id} title={p.work.title} />
        <span className="perf-card__len">{formatMs(length)}</span>
      </Link>
      <div className="perf-card__body">
        <p className="perf-card__tags">
          <Pill tone={p.kind === "stream" ? "petal" : "info"}>{kindLabel(p.kind)}</Pill>
          {p.performed_at && <span className="perf-card__date">{formatDate(p.performed_at)}</span>}
          {!p.available && (
            <Pill tone="danger" icon="warn">
              {UNAVAILABLE_TEXT[p.unavailable_reason ?? "unknown"] ?? "利用不能"}
            </Pill>
          )}
        </p>
        <h3 className="perf-card__title">
          <Link to={`/performances/${p.id}`}>{p.work.title}</Link>
        </h3>
        <p className="perf-card__performer">{performerNames(p)}</p>
        {note && <p className="perf-card__note">{note}</p>}
        <div className="perf-card__actions">
          <EnqueueButton performance={p} />
          <SaveButton target={{ performance_id: p.id }} label={`「${p.work.title}」（${performerNames(p)}）`} />
        </div>
      </div>
    </li>
  );
}

/* ---------------------------------------------------------------- ここ好きの時刻（押すと、その瞬間の詳細と元動画リンク。共有プレイヤーは動かさない） */

export function MomentChip({ moment, performance }: { moment: Moment; performance: Performance }) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const range = moment.end_ms !== null ? `${formatMs(moment.position_ms)}〜${formatMs(moment.end_ms)}` : formatMs(moment.position_ms);
  const source = sourcePosition(segmentStartFor(moment, performance), moment.position_ms);
  return (
    <span className="moment">
      <button type="button" className="moment__chip" aria-expanded={open} aria-controls={panelId} onClick={() => setOpen((v) => !v)}>
        <Icon name="sparkle" className="moment__icon" />
        <span className="visually-hidden">ここ好き </span>
        {range}
      </button>
      <span id={panelId} className="moment__panel" hidden={!open}>
        {moment.body && <span className="moment__body">「{moment.body}」</span>}
        <span className="moment__meta">
          {moment.author_name && <>{moment.author_name} さん ・ </>}
          元動画では {formatMs(source)}
        </span>
        <a className="moment__link" href={watchUrl(performance.segment.video_id, source)} target="_blank" rel="noopener noreferrer">
          YouTubeでこの瞬間を開く
          <Icon name="external" className="inline-icon" />
          <span className="visually-hidden">（新しいタブ）</span>
        </a>
        {moment.post_id && <Link className="moment__link" to={`/posts/${moment.post_id}`}>このここ好きだけを見る</Link>}
      </span>
    </span>
  );
}

/** ここ好きが指す版の区間開始。区間が直されたあとも、元の版の値（source_position_ms）を優先する */
function segmentStartFor(m: Moment, p: Performance): number {
  if (m.source_position_ms !== undefined && m.source_position_ms !== null) return m.source_position_ms - m.position_ms;
  return p.segment.start_ms;
}

/* ---------------------------------------------------------------- 投稿のカード（参照元の trio：左に3色の帯を順に） */

export function PostCard({
  post,
  showPerformance = true,
  showVisibility = false,
  detail = false,
}: {
  post: Post;
  showPerformance?: boolean;
  showVisibility?: boolean;
  detail?: boolean;
}) {
  const body = visibleBody(post);
  const p = post.performance;
  if (body === null) {
    return (
      <li className="post-card post-card--gone">
        <p className="post-card__gone">
          <Icon name="eyeOff" className="inline-icon" />
          この投稿は削除されたか、非表示になっています。
        </p>
        {p && (
          <p className="post-card__gone-link">
            歌唱は残っています：
            <Link to={`/performances/${p.id}`}>
              {p.work.title}／{performerNames(p)}
            </Link>
          </p>
        )}
      </li>
    );
  }
  return (
    <li className="post-card">
      <div className="post-card__head">
        <span className="post-card__author">{post.author_name}</span>
        <Link className="post-card__time" to={`/posts/${post.id}`} aria-label={`${post.author_name} さんの投稿を開く`}>{formatAgo(post.created_at)}</Link>
        {post.visibility === "private" && <Pill tone="draft">自分だけ</Pill>}
        {showVisibility && post.visibility === "members" && <Pill tone="calm">メンバー限定</Pill>}
        <PostMenu post={post} />
      </div>
      {body ? <p className="post-card__body">{body}</p> : <p className="post-card__body post-card__body--short">（ひとことなし）</p>}
      {p && (post.moments ?? []).length > 0 && (
        <p className="post-card__moments">
          {(post.moments ?? []).map((m) => (
            detail ? <MomentSpotlight key={m.id} moment={m} performance={m.performance ?? p} /> : <MomentChip key={m.id} moment={m} performance={p} />
          ))}
        </p>
      )}
      {showPerformance && p && (
        <div className="post-card__perf">
          <Thumb videoId={p.segment.video_id} title={p.work.title} size="s" />
          <div>
            <Link to={`/performances/${p.id}`} className="post-card__perf-title">
              {p.work.title}
            </Link>
            <span className="post-card__perf-sub">
              {performerNames(p)} ・ {kindLabel(p.kind)}
            </span>
          </div>
        </div>
      )}
      {p && (
        <div className="post-card__actions">
          <SaveButton target={{ post_id: post.id }} label={`${post.author_name} さんの投稿`} />
          {!detail && <EnqueueButton performance={p} post={post} />}
          {post.visibility === "members" && (post.moments?.length ?? 0) > 0 && <SharePost postId={post.id} />}
          <Link className="btn btn-ghost btn-small" to={detail ? `/performances/${p.id}` : `/posts/${post.id}`}>
            {detail ? "曲のページへ" : "投稿を見る"}
          </Link>
        </div>
      )}
    </li>
  );
}

function MomentSpotlight({ moment, performance }: { moment: Moment; performance: Performance }) {
  const me = useMe();
  const source = sourcePosition(segmentStartFor(moment, performance), moment.position_ms);
  const range = formatMs(moment.position_ms) + (moment.end_ms !== null ? `〜${formatMs(moment.end_ms)}` : "");
  return <span className="moment-spotlight">
    <span className="moment-spotlight__label"><Icon name="sparkle" />好きな瞬間</span>
    <span className="moment-spotlight__time">{range}</span>
    <a className="btn btn-secondary" href={watchUrl(moment.segment?.video_id ?? performance.segment.video_id, source)} target="_blank" rel="noopener noreferrer">
      <Icon name="external" className="btn-icon" />YouTubeでこの瞬間を開く<span className="visually-hidden">（新しいタブ）</span>
    </a>
    <span className="muted">元動画の {formatMs(source)} から。VCの再生は動きません。</span>
    {me?.id === moment.owner_id && <Link to={`/moments/${moment.id}`}>このここ好きを編集する</Link>}
  </span>;
}

/* ---------------------------------------------------------------- 投稿のメニュー（通報・運営の非表示・自分の投稿の削除） */

function PostMenu({ post }: { post: Post }) {
  const me = useMe();
  const { api } = useServices();
  const [open, setOpen] = useState(false);
  // 管理者の非表示は、契約では通報に対して行う（PUT /reports/{id}）。投稿から直接は行わない（通報の確認の画面から）
  const [dialog, setDialog] = useState<null | "report" | "delete">(null);
  const ref = useRef<HTMLDivElement>(null);
  const menuId = useId();
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        ref.current?.querySelector<HTMLButtonElement>(".post-menu__btn")?.focus();
      }
    };
    document.addEventListener("click", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  if (!me) return null;
  const mine = post.owner_id === me.id;
  // 自分の投稿でも、他人の投稿でも、出せる項目が無ければメニューを出さない
  if (mine ? !api.isConnected("posts.delete") : !api.isConnected("reports.create")) return null;
  return (
    <div className="post-menu" ref={ref}>
      <button
        type="button"
        className="icon-btn icon-btn--small post-menu__btn"
        aria-expanded={open}
        aria-controls={menuId}
        aria-label={`${post.author_name} さんの投稿のその他の操作`}
        onClick={() => setOpen((v) => !v)}
      >
        <Icon name="more" />
      </button>
      <ul id={menuId} className="post-menu__list" hidden={!open}>
        {!mine && (
          <li>
            <button type="button" onClick={() => (setOpen(false), setDialog("report"))}>
              <Icon name="flag" />
              運営に通報
            </button>
          </li>
        )}
        {mine && (
          <li>
            <button type="button" onClick={() => (setOpen(false), setDialog("delete"))}>
              <Icon name="trash" />
              投稿を削除
            </button>
          </li>
        )}
      </ul>
      <ReportDialog open={dialog === "report"} post={post} onClose={() => setDialog(null)} />
      <ConfirmAction
        open={dialog === "delete"}
        title="投稿を削除しますか？"
        body="本文は誰にも見えなくなります。歌唱そのもの、キューに入っている曲、ほかの人の保存は残ります。"
        confirmLabel="削除する"
        op="posts.delete"
        action={() => api.deletePost(post.id)}
        onClose={() => setDialog(null)}
        onDone={() => noteRedacted({ id: post.id, unavailable: true })}
      />
    </div>
  );
}

export function ConfirmAction({
  open,
  title,
  body,
  confirmLabel,
  op,
  action,
  onClose,
  onDone,
}: {
  open: boolean;
  title: string;
  body: string;
  confirmLabel: string;
  op: Parameters<typeof useAction>[0];
  action: () => Promise<unknown>;
  onClose: () => void;
  onDone?: () => void;
}) {
  const act = useAction(op, action);
  return (
    <Dialog
      open={open}
      title={title}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            やめる
          </button>
          <button
            type="button"
            className="btn btn-danger"
            disabled={act.running || !act.connected}
            aria-busy={act.running || undefined}
            onClick={async () => {
              const r = await act.run();
              if (r.ok) {
                onClose();
                onDone?.();
              }
            }}
          >
            {confirmLabel}
          </button>
        </>
      }
    >
      <p>{body}</p>
      {!act.connected && <p className="inline-note inline-note--warn">この操作はまだAPIに接続されていません。</p>}
      {act.error && (
        <p className="inline-note inline-note--danger" role="alert">
          {act.error}
        </p>
      )}
    </Dialog>
  );
}

function ReportDialog({ open, post, onClose }: { open: boolean; post: Post; onClose: () => void }) {
  const { api } = useServices();
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const act = useAction("reports.create", () => api.report({ post_id: post.id, reason: reason.trim() }));
  const close = () => {
    onClose();
    setTimeout(() => {
      setDone(false);
      setReason("");
      setError(null);
    }, 200);
  };
  return (
    <Dialog
      open={open}
      title="運営に通報"
      onClose={close}
      footer={
        done ? (
          <button type="button" className="btn btn-primary" onClick={close}>
            閉じる
          </button>
        ) : (
          <>
            <button type="button" className="btn btn-secondary" onClick={close}>
              やめる
            </button>
            <button
              type="submit"
              form={`report-${post.id}`}
              className="btn btn-primary"
              disabled={act.running || !act.connected}
              aria-busy={act.running || undefined}
            >
              通報を送る
            </button>
          </>
        )
      }
    >
      {done ? (
        <p role="status">通報を受け付けました{act.suffix}。内容は管理者だけが見ます。投稿者に通報したことは伝わりません。</p>
      ) : (
        <form
          id={`report-${post.id}`}
          onSubmit={async (e) => {
            e.preventDefault();
            if (!reason.trim()) return setError("理由を書いてください。");
            setError(null);
            const r = await act.run();
            if (r.ok) setDone(true);
          }}
        >
          <p className="dialog__lead">{post.author_name} さんの投稿について、管理者に知らせます。通報の内容は管理者だけが見ます。</p>
          {!act.connected && <p className="inline-note inline-note--warn">通報はまだAPIに接続されていません。</p>}
          <Field label="理由" hint="何が困るのかを、短く書いてください。" error={error ?? act.error} required>
            {({ id, describedBy, invalid }) => (
              <textarea
                id={id}
                className="input textarea"
                rows={4}
                maxLength={2000}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
              />
            )}
          </Field>
        </form>
      )}
    </Dialog>
  );
}
